import { Persistence } from "../../../config/constants";
import { TickLayerName } from "../../../config/tickLayers";
import { BaseEntity } from "../../../core/entity/BaseEntity";
import { Entity } from "../../../core/entity/Entity";
import { on } from "../../../core/entity/handler";
import { TRUCK_BODY_WIDTH } from "../../../game/lot";
import { doorCenterX } from "../../../game/ShopInfo";
import {
  dueScene,
  sceneById,
  sceneNeedsTheFloor,
  StoryBeat,
  StoryScene,
  StorySceneId,
  wantById,
} from "../../../game/story";
import { Vector } from "../../../game/Vectors";
import { beginScene, finishScene } from "../../../sim/commands/story-commands";
import { MachineEntity } from "../../../sim/entities/MachineEntity";
import { MaterialPileEntity } from "../../../sim/entities/MaterialPileEntity";
import { Player } from "../../../sim/entities/Player";
import { projectTutorialFacts } from "../../../sim/projection";
import { ShopInfo } from "../../../sim/singletons/ShopInfo";
import { StoryTracker } from "../../../sim/singletons/StoryTracker";
import { ShellStore } from "../../ShellStore";
import { BenchDive } from "../bench/BenchDive";
import { TripTheater } from "../TripTheater";
import { currentVenue } from "../venue";
import { WifeView } from "./WifeView";

/**
 * The story's stage manager: performs the scene the story owes, beat by
 * beat, between `beginScene` and `finishScene` (story-commands.ts).
 *
 * Each tick it asks the script which scene is owed (`dueScene`, over
 * the sim's own facts) and, when the shop is ready for one — the truck
 * parked, no dialog up, and the player standing on the floor if she
 * walks in (`sceneNeedsTheFloor`; a thought plays over the bench view
 * too, since the bench steps are worked there and their thoughts are
 * owed the moment the work is done) — begins it. The one scene owed
 * off the floor, the thought from the cab out scavenging, begins over
 * the trip's screen instead. From then on it runs the beats: an
 * `enter` spawns Wife and walks her in along the driveway route; `say`,
 * `phone`, and `think` put a line up in the HUD's box (StoryLayer) and
 * wait for `advance`; `leave` walks her out. Past the last beat the
 * scene is finished and recorded, and the world gets its keys back.
 *
 * The world keeps drawing throughout (the walk is the point) but the
 * clock is stopped — the StoryTracker holds that — and
 * the input entities stand down while `playing()`. The pause menu can
 * still open during a walk, and this entity is pausable so the walk
 * freezes with it.
 *
 * A save loaded mid-scene has no scene playing (the tracker's `playing`
 * is transient) and the scene is still owed, so it starts over from its
 * first beat — the Pokémon rule: you can't skip a scene, only its lines.
 */

/** One beat as the HUD draws it: which scene, which line, how far along. */
export interface StoryLine {
  readonly sceneId: StorySceneId;
  readonly index: number;
  readonly count: number;
  readonly beat: Extract<StoryBeat, { kind: "say" | "phone" | "think" }>;
}

/** How far off the bottom of the indoor framing she starts, in cells. */
const OFFSCREEN_BELOW = 8;
/** How far from the piece she stops to look at it, in cells. */
const LOOKING_DISTANCE = 1.3;

export class CutsceneDirector extends BaseEntity implements Entity {
  id = "cutsceneDirector";
  persistenceLevel: number = Persistence.Permanent;
  // Runs after the sim's own tick so a scene begins on the tick its
  // condition first holds, and the milestone pass has already turned
  // over whatever the last scene revealed.
  tickLayer: TickLayerName = "main";
  pausable = true;

  private scene: StoryScene | null = null;
  private beatIndex = 0;
  private wife: WifeView | null = null;
  /** The route she walked in on, kept for the walk out. */
  private route: ReadonlyArray<Vector> | null = null;

  /** Whether a scene is up right now — the keys are the story's. */
  playing(): boolean {
    return this.game.entities.tryGetSingleton(StoryTracker)?.playing != null;
  }

  /** The line the box should show, or null between lines. */
  currentLine(): StoryLine | null {
    if (!this.scene) return null;
    const beat = this.scene.beats[this.beatIndex];
    if (!beat || beat.kind === "enter" || beat.kind === "leave") return null;
    return {
      sceneId: this.scene.id,
      index: this.beatIndex,
      count: this.scene.beats.length,
      beat,
    };
  }

  /** The box says its line is read: on to the next beat. */
  advance(): void {
    if (this.currentLine() === null) return;
    this.step();
  }

  private step(): void {
    this.beatIndex += 1;
    this.bump();
  }

  private bump(): void {
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  @on("tick")
  onTick() {
    const tracker = this.game.entities.tryGetSingleton(StoryTracker);
    if (!tracker) {
      this.teardown();
      return;
    }
    if (tracker.playing === null) {
      // Nothing up: clear any leftovers (a skipped scene) and see
      // whether one is owed.
      if (this.scene) this.teardown();
      this.tryBegin();
      return;
    }
    if (this.scene?.id !== tracker.playing) {
      this.start(tracker.playing);
    }
    this.perform();
  }

  /**
   * Begin the owed scene if the shop is ready for it — or, out
   * scavenging, if the trip is parked at its decision: the script only
   * owes a `fromTheCab` thought there, and it plays over the trip's
   * own screen (which is a dialog, so the modal check doesn't apply).
   * At home a thought plays wherever the player is, the bench view
   * included; a scene she walks into waits until he stands back up.
   */
  private tryBegin(): void {
    const game = this.game;
    const player = game.entities.tryGetSingleton(Player);
    if (!player) return;
    if (player.away?.kind === "scavenging") {
      if (player.away.phase.kind !== "deciding") return;
    } else {
      if (currentVenue(game) !== "shop") return;
      if (game.entities.tryGetSingleton(ShellStore)?.modalOpen) return;
      const stage = game.entities.tryGetSingleton(TripTheater)?.stage();
      if (stage !== undefined && stage !== "parked") return;
    }
    const owed = dueScene(projectTutorialFacts(game));
    if (!owed) return;
    if (
      sceneNeedsTheFloor(owed) &&
      game.entities.tryGetSingleton(BenchDive)?.openBenchKey != null
    ) {
      return;
    }
    beginScene(game, owed.id);
  }

  private start(id: StorySceneId): void {
    this.teardown();
    this.scene = sceneById(id);
    this.beatIndex = 0;
    this.bump();
  }

  /** Run the current beat; walks advance themselves, lines wait. */
  private perform(): void {
    const scene = this.scene;
    if (!scene) return;
    const beat = scene.beats[this.beatIndex];
    if (!beat) {
      this.teardown();
      finishScene(this.game);
      return;
    }
    switch (beat.kind) {
      case "enter": {
        if (!this.wife) {
          this.route = this.routeIn(scene);
          this.wife = this.game.addEntity(new WifeView(this.route));
        }
        if (this.wife.arrived) this.step();
        return;
      }
      case "leave": {
        if (!this.wife) {
          this.step();
          return;
        }
        if (!this.leaving) {
          this.leaving = true;
          this.wife.leave();
        }
        if (this.wife.arrived) {
          this.wife.destroy();
          this.wife = null;
          this.leaving = false;
          this.step();
        }
        return;
      }
      default:
        // A line: the box calls advance().
        return;
    }
  }

  private leaving = false;

  private teardown(): void {
    if (this.wife) {
      this.wife.destroy();
      this.wife = null;
    }
    this.leaving = false;
    this.scene = null;
    this.beatIndex = 0;
    this.route = null;
  }

  /**
   * Her way in: off the bottom of the framing beside the truck, up the
   * grass strip to the tailgate aisle, through the doorway, then
   * straight to what she came to see — the scene's piece if it's out
   * where she can find it, else the player.
   */
  private routeIn(scene: StoryScene): Vector[] {
    const shopInfo = this.game.entities.getSingleton(ShopInfo).info;
    const doorX = doorCenterX(shopInfo);
    const stripX = doorX + TRUCK_BODY_WIDTH / 2 + 1.4;
    const bottom = shopInfo.size[1];
    const subject = this.subjectPoint(scene);
    // Stop short of the subject, on the door side of it, inside the walls.
    const stop: Vector = [
      Math.min(Math.max(subject[0], 0.8), shopInfo.size[0] - 0.8),
      Math.min(subject[1] + LOOKING_DISTANCE, bottom - 0.8),
    ];
    return [
      [stripX, bottom + OFFSCREEN_BELOW],
      [stripX, bottom + 1.0],
      [doorX, bottom + 0.9],
      [doorX, bottom - 1.2],
      stop,
    ];
  }

  /** Where the scene's piece lies, or where the player stands. */
  private subjectPoint(scene: StoryScene): Vector {
    const game = this.game;
    const player = game.entities.getSingleton(Player);
    if (scene.subject === undefined) return [...player.position] as Vector;
    const type = wantById(scene.subject).productType;
    for (const pile of game.entities.byConstructor(MaterialPileEntity)) {
      if (pile.material.type === type) return [...pile.position] as Vector;
    }
    for (const machine of game.entities.byConstructor(MachineEntity)) {
      const holds = [
        ...machine.state.outputMaterials,
        ...machine.state.inputMaterials,
        ...(machine.state.storedMaterials ?? []),
      ].some((material) => material.type === type);
      if (holds) {
        const cell =
          machine.view().absoluteOperationPosition ?? machine.state.position;
        return [cell[0] + 0.5, cell[1] + 0.5];
      }
    }
    return [...player.position] as Vector;
  }
}
